// src/pages/couriers/components/ConfigModal.jsx
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { 
  Settings, 
  X, 
  Save, 
  RefreshCw, 
  Key, 
  Eye, 
  EyeOff, 
  Hash, 
  Package,
  Webhook,
  Copy,
  Check
} from 'lucide-react';
import styles from './ConfigModal.module.css';

const ConfigModal = ({
  isOpen,
  onClose,
  courier,
  onSave,
  isSubmitting,
  onEnableWebhook,
  onDisableWebhook,
  onRegenerateWebhook
}) => {
  const { t } = useLocalization();
  
  const [configForm, setConfigForm] = useState({
    accountName: '',
    isActive: true,
    apiKey: '',
    apiConfigEnabled: false,
    apiUploadEnabled: true,
    waybillRangeStart: '',
    waybillRangeEnd: '',
    webhookEnabled: false,
    apiConfig: {}
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [configErrors, setConfigErrors] = useState({});
  const [copied, setCopied] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);

  useEffect(() => {
    if (courier) {
      setConfigForm({
        accountName: courier.accountName || '',
        isActive: courier.isActive ?? true,
        apiKey: courier.apiKey || '',
        apiConfigEnabled: courier.apiConfigEnabled || false,
        apiUploadEnabled: courier.apiUploadEnabled ?? true,
        waybillRangeStart: courier.waybillRangeStart || '',
        waybillRangeEnd: courier.waybillRangeEnd || '',
        webhookEnabled: courier.webhookEnabled || false,
        apiConfig: courier.apiConfig || {}
      });
      setShowApiKey(false);
      setConfigErrors({});
      setCopied(false);
    }
  }, [courier]);

  if (!isOpen || !courier) return null;

  const courierCompany = courier.CourierCompany;
  const apiAllowed = courierCompany?.apiAllowed;
  const webHookAllowed = courierCompany?.webHookAllowed;
  const isAutoWaybill = courierCompany?.isAutoWaybill;
  const additionalFields = courierCompany?.apiConfig?.additionalFields;

  const showWaybillSection = !configForm.apiConfigEnabled || !isAutoWaybill || !configForm.apiUploadEnabled;

  const handleFormChange = (field, value) => {
    setConfigForm(prev => ({ ...prev, [field]: value }));
    if (configErrors[field]) {
      setConfigErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleApiConfigChange = (fieldName, value) => {
    setConfigForm(prev => ({
      ...prev,
      apiConfig: {
        ...prev.apiConfig,
        [fieldName]: value
      }
    }));
    if (configErrors[`apiConfig.${fieldName}`]) {
      setConfigErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`apiConfig.${fieldName}`];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!configForm.accountName.trim()) {
      errors.accountName = t('couriers.accountNameRequired');
    }

    if (configForm.apiConfigEnabled && !configForm.apiKey && !courier.apiKey) {
      errors.apiKey = t('couriers.apiKeyRequired');
    }

    // Validate additional fields if API is enabled
    if (configForm.apiConfigEnabled && additionalFields) {
      Object.keys(additionalFields).forEach(fieldName => {
        const fieldConfig = additionalFields[fieldName];
        const fieldValue = configForm.apiConfig[fieldName];
        
        // Check if field is required (all additional fields are required)
        if (!fieldValue && fieldValue !== false && fieldValue !== 0) {
          errors[`apiConfig.${fieldName}`] = `${fieldConfig.label} is required`;
        }
      });
    }

    if (configForm.waybillRangeStart && configForm.waybillRangeEnd) {
      const start = parseInt(configForm.waybillRangeStart.match(/\d+$/)?.[0] || '0');
      const end = parseInt(configForm.waybillRangeEnd.match(/\d+$/)?.[0] || '0');
      
      if (start > end) {
        errors.waybillRangeEnd = t('couriers.rangeError');
      }
    }

    setConfigErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updateData = {};
    
    if (configForm.accountName.trim() !== (courier.accountName || '')) {
      updateData.accountName = configForm.accountName.trim();
    }
    if (configForm.isActive !== courier.isActive) {
      updateData.isActive = configForm.isActive;
    }
    if (configForm.apiKey !== (courier.apiKey || '')) {
      updateData.apiKey = configForm.apiKey;
    }
    if (configForm.apiConfigEnabled !== courier.apiConfigEnabled) {
      updateData.apiConfigEnabled = configForm.apiConfigEnabled;
    }
    if (configForm.apiUploadEnabled !== (courier.apiUploadEnabled ?? true)) {
      updateData.apiUploadEnabled = configForm.apiUploadEnabled;
    }
    if (configForm.waybillRangeStart !== (courier.waybillRangeStart || '')) {
      updateData.waybillRangeStart = configForm.waybillRangeStart;
    }
    if (configForm.waybillRangeEnd !== (courier.waybillRangeEnd || '')) {
      updateData.waybillRangeEnd = configForm.waybillRangeEnd;
    }

    // Include apiConfig if there are additional fields and API is enabled
    if (configForm.apiConfigEnabled && additionalFields && Object.keys(additionalFields).length > 0) {
      updateData.apiConfig = configForm.apiConfig;
    }

    onSave(updateData);
  };

  const handleWebhookToggle = async () => {
    setWebhookLoading(true);
    try {
      if (configForm.webhookEnabled) {
        await onDisableWebhook();
        setConfigForm(prev => ({ ...prev, webhookEnabled: false }));
      } else {
        await onEnableWebhook();
        setConfigForm(prev => ({ ...prev, webhookEnabled: true }));
      }
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleRegenerateWebhook = async () => {
    setWebhookLoading(true);
    try {
      await onRegenerateWebhook();
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    if (courier.webhookUrl) {
      navigator.clipboard.writeText(courier.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderAdditionalField = (fieldName, fieldConfig) => {
    const value = configForm.apiConfig[fieldName];
    const error = configErrors[`apiConfig.${fieldName}`];

    switch (fieldConfig.type) {
      case 'text':
        return (
          <div key={fieldName} className={styles.formGroup}>
            <label>{fieldConfig.label}</label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleApiConfigChange(fieldName, e.target.value)}
              placeholder={`Enter ${fieldConfig.label}`}
              className={error ? styles.error : ''}
            />
            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        );

      case 'password':
        return (
          <div key={fieldName} className={styles.formGroup}>
            <label>{fieldConfig.label}</label>
            <input
              type="password"
              value={value || ''}
              onChange={(e) => handleApiConfigChange(fieldName, e.target.value)}
              placeholder={`Enter ${fieldConfig.label}`}
              className={error ? styles.error : ''}
            />
            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldName} className={styles.formGroup}>
            <label>{fieldConfig.label}</label>
            <select
              value={value || ''}
              onChange={(e) => handleApiConfigChange(fieldName, e.target.value)}
              className={error ? styles.error : ''}
            >
              <option value="">Select {fieldConfig.label}</option>
              {fieldConfig.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldName} className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <span>{fieldConfig.label}</span>
              <div className={styles.toggleWrapper}>
                <input
                  type="checkbox"
                  checked={value || false}
                  onChange={(e) => handleApiConfigChange(fieldName, e.target.checked)}
                  className={styles.toggleInput}
                />
                <span className={styles.toggleSlider}></span>
              </div>
            </label>
            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            <Settings size={20} />
            {t('couriers.configureModalTitle')} {courier.accountName}
          </h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* General Settings */}
          <div className={styles.configSection}>
            <h3>{t('couriers.generalSettings')}</h3>
            <div className={styles.formGroup}>
              <label>{t('couriers.accountName')}</label>
              <input
                type="text"
                value={configForm.accountName}
                onChange={(e) => handleFormChange('accountName', e.target.value)}
                placeholder={t('couriers.accountNamePlaceholder')}
                className={configErrors.accountName ? styles.error : ''}
              />
              {configErrors.accountName && <span className={styles.errorText}>{configErrors.accountName}</span>}
              <small className={styles.fieldHint}>{t('couriers.accountNameHint')}</small>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.toggleLabel}>
                <span>{t('couriers.activeStatusLabel')}</span>
                <div className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    checked={configForm.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                    className={styles.toggleInput}
                  />
                  <span className={styles.toggleSlider}></span>
                </div>
              </label>
              <small className={styles.fieldHint}>
                {t('couriers.activeStatusHint')}
              </small>
            </div>
          </div>

          {/* API Configuration */}
          {apiAllowed && (
            <div className={styles.configSection}>
              <h3>
                <Key size={16} />
                {t('couriers.apiConfiguration')}
              </h3>
              
              <div className={styles.formGroup}>
                <label className={styles.toggleLabel}>
                  <span>{t('couriers.enableApiIntegration')}</span>
                  <div className={styles.toggleWrapper}>
                    <input
                      type="checkbox"
                      checked={configForm.apiConfigEnabled}
                      onChange={(e) => handleFormChange('apiConfigEnabled', e.target.checked)}
                      className={styles.toggleInput}
                    />
                    <span className={styles.toggleSlider}></span>
                  </div>
                </label>
                <small className={styles.fieldHint}>
                  {t('couriers.enableApiHint')}
                </small>
              </div>

              {configForm.apiConfigEnabled && (
                <>
                  <div className={styles.formGroup}>
                    <label>{t('couriers.apiKey')}</label>
                    <div className={styles.passwordInputWrapper}>
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={configForm.apiKey}
                        onChange={(e) => handleFormChange('apiKey', e.target.value)}
                        placeholder={t('couriers.apiKeyPlaceholder')}
                        className={configErrors.apiKey ? styles.error : ''}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className={styles.passwordToggle}
                      >
                        {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {configErrors.apiKey && (
                      <span className={styles.errorText}>{configErrors.apiKey}</span>
                    )}
                    <small className={styles.fieldHint}>
                      {t('couriers.apiKeyHint')}
                    </small>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.toggleLabel}>
                      <span>{t('couriers.enableApiUpload')}</span>
                      <div className={styles.toggleWrapper}>
                        <input
                          type="checkbox"
                          checked={configForm.apiUploadEnabled}
                          onChange={(e) => handleFormChange('apiUploadEnabled', e.target.checked)}
                          className={styles.toggleInput}
                        />
                        <span className={styles.toggleSlider}></span>
                      </div>
                    </label>
                    <small className={styles.fieldHint}>
                      {t('couriers.enableApiUploadHint')}
                    </small>
                  </div>

                  {/* Additional API Configuration Fields */}
                  {additionalFields && Object.keys(additionalFields).length > 0 && (
                    <div className={styles.additionalFieldsSection}>
                      <h4 className={styles.subsectionTitle}>
                        {t('couriers.additionalApiConfig')}
                      </h4>
                      {Object.entries(additionalFields).map(([fieldName, fieldConfig]) =>
                        renderAdditionalField(fieldName, fieldConfig)
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Waybill Range Configuration */}
          {showWaybillSection && (
            <div className={styles.configSection}>
              <h3>
                <Hash size={16} />
                {t('couriers.waybillConfiguration')}
              </h3>
              <p className={styles.sectionDescription}>
                {t('couriers.waybillDescription')}
              </p>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{t('couriers.waybillRangeStart')}</label>
                  <input
                    type="text"
                    value={configForm.waybillRangeStart}
                    onChange={(e) => handleFormChange('waybillRangeStart', e.target.value)}
                    placeholder={t('couriers.waybillRangeStartPlaceholder')}
                    className={configErrors.waybillRangeStart ? styles.error : ''}
                  />
                  {configErrors.waybillRangeStart && (
                    <span className={styles.errorText}>{configErrors.waybillRangeStart}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>{t('couriers.waybillRangeEnd')}</label>
                  <input
                    type="text"
                    value={configForm.waybillRangeEnd}
                    onChange={(e) => handleFormChange('waybillRangeEnd', e.target.value)}
                    placeholder={t('couriers.waybillRangeEndPlaceholder')}
                    className={configErrors.waybillRangeEnd ? styles.error : ''}
                  />
                  {configErrors.waybillRangeEnd && (
                    <span className={styles.errorText}>{configErrors.waybillRangeEnd}</span>
                  )}
                </div>
              </div>

              {courier.lastWaybillUsed && (
                <div className={styles.infoCard}>
                  <Package size={16} />
                  <div>
                    <strong>{t('couriers.lastWaybillUsed')}:</strong> {courier.lastWaybillUsed}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Webhook Configuration */}
          {webHookAllowed && (
            <div className={styles.configSection}>
              <h3>
                <Webhook size={16} />
                {t('couriers.webhookConfiguration')}
              </h3>
              
              <div className={styles.formGroup}>
                <label className={styles.toggleLabel}>
                  <span>{t('couriers.enableWebhook')}</span>
                  <div className={styles.toggleWrapper}>
                    <input
                      type="checkbox"
                      checked={configForm.webhookEnabled}
                      onChange={handleWebhookToggle}
                      disabled={webhookLoading}
                      className={styles.toggleInput}
                    />
                    <span className={styles.toggleSlider}></span>
                  </div>
                </label>
                <small className={styles.fieldHint}>
                  {t('couriers.enableWebhookHint')}
                </small>
              </div>

              {configForm.webhookEnabled && courier.webhookUrl && (
                <div className={styles.webhookUrlSection}>
                  <label>{t('couriers.webhookUrl')}</label>
                  <div className={styles.webhookUrlWrapper}>
                    <input
                      type="text"
                      value={courier.webhookUrl}
                      readOnly
                      className={styles.webhookUrlInput}
                    />
                    <button
                      type="button"
                      onClick={handleCopyWebhookUrl}
                      className={styles.copyButton}
                      title={t('couriers.webhookUrl')}
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                  <small className={styles.fieldHint}>
                    {t('couriers.webhookUrlHint')}
                  </small>
                  
                  <button
                    type="button"
                    onClick={handleRegenerateWebhook}
                    disabled={webhookLoading}
                    className={styles.regenerateButton}
                  >
                    {webhookLoading ? (
                      <>
                        <RefreshCw size={14} className={styles.spinning} />
                        {t('couriers.regenerating')}
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} />
                        {t('couriers.regenerateWebhook')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            onClick={onClose}
            className={styles.secondaryButton}
            disabled={isSubmitting}
          >
            {t('couriers.cancel')}
          </button>
          <button
            onClick={handleSave}
            className={styles.primaryButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <RefreshCw size={16} className={styles.spinning} />
                {t('couriers.saving')}
              </>
            ) : (
              <>
                <Save size={16} />
                {t('couriers.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigModal;